/**
 * useRegister Hook
 * Custom hook for handling registration logic
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { wait, encode, decode, C, storage, isEmail } from '@/utils';
import { connect, getSocket } from '@/utils/socketInstance';
import { useToast } from './useToast';

export const useRegister = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpSection, setShowOtpSection] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const resendTimerIntervalRef = useRef(null);

  const verifyReferralCode = async (referral) => {
    if (!referral || !referral.trim()) return true;
    
    try {
      const response = await fetch(`https://api.jackopot.com/verify-referral-code/${referral}`);
      const data = await response.json();
      return data.valid;
    } catch (error) {
      console.error('Error verifying referral code:', error);
      return false;
    }
  };

  const sendOtp = async (email) => {
    if (!email) {
      showError('Please enter an email address');
      return false;
    }

    if (!isEmail(email)) {
      showError('Please enter valid email address');
      return false;
    }

    try {
      const response = await axios.post('https://api.jackopot.com/email/otp/send', {
        email: email,
        purpose: 'register'
      });

      if (response.data.success) {
        setOtpSent(true);
        setShowOtpSection(true);
        setResendTimer(30);
        showSuccess('OTP sent successfully to your email. Please check Junk or Spam folders.');
        startResendTimer();
        return true;
      } else {
        showError(response.data.error || 'Failed to send OTP');
        return false;
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      showError('An error occurred while sending OTP');
      return false;
    }
  };

  const verifyOtp = async (email, otpCode) => {
    if (!otpCode) {
      showError('Please enter the OTP');
      return false;
    }

    try {
      const response = await axios.post('https://api.jackopot.com/email/otp/verify', {
        email: email,
        otp: otpCode,
        purpose: 'register'
      });

      if (response.data.success) {
        setOtpVerified(true);
        setShowOtpSection(false);
        if (resendTimerIntervalRef.current) {
          clearInterval(resendTimerIntervalRef.current);
          resendTimerIntervalRef.current = null;
        }
        showSuccess('Email verified successfully!');
        return true;
      } else {
        showError(response.data.error || 'Invalid OTP');
        return false;
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      showError('An error occurred while verifying OTP');
      return false;
    }
  };

  const resendOtp = async (email) => {
    if (resendTimer > 0) {
      showInfo(`Please wait ${resendTimer} seconds before requesting another OTP`);
      return false;
    }

    try {
      const response = await axios.post('https://api.jackopot.com/email/otp/resend', {
        email: email,
        purpose: 'register'
      });

      if (response.data.success) {
        setResendTimer(30);
        showSuccess('OTP resent successfully');
        startResendTimer();
        return true;
      } else {
        showError(response.data.error || 'Failed to resend OTP');
        return false;
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      showError('An error occurred while resending OTP');
      return false;
    }
  };

  const startResendTimer = () => {
    if (resendTimerIntervalRef.current) {
      clearInterval(resendTimerIntervalRef.current);
    }

    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          resendTimerIntervalRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    resendTimerIntervalRef.current = interval;
  };

  const setRegister = (data) => {
    console.log('Received registration response:', data);

    if (data.error) {
      setIsLoading(false);
      showError(data.error);
      return;
    }

    if (data.status) {
      console.log('Registration successful');
      setIsLoading(false);
      showSuccess('Registration successful!');

      // Auto-login after successful registration (like old code)
      wait(1000).then(() => {
        const currentSocket = getSocket();
        if (currentSocket) {
          currentSocket.emit(C.LOGIN_USER, encode({
            username: data.name,
            password: data.password,
            recaptcha: 'google'
          }));
        }
      });

      localStorage.setItem('showLoginSuccessModal', 'true');
    }
  };

  // Setup socket listeners
  useEffect(() => {
    // Get current socket instance
    let currentSocket = getSocket();
    if (!currentSocket) {
      currentSocket = connect(storage.getKey('token'));
    }

    // Simple socket event listeners like the working code
    if (currentSocket) {
      console.log('Setting up register listener on socket:', currentSocket.id || 'pending');
      currentSocket.on(C.REGISTER_USER, data => {
        console.log('Received registration response data (raw):', data);
        const decoded = decode(data);
        console.log('Decoded registration response:', decoded);
        setRegister(decoded);
      });
    } else {
      console.error('Socket not available for register listener');
    }

    return () => {
      if (currentSocket) {
        currentSocket.off(C.REGISTER_USER);
      }
      if (resendTimerIntervalRef.current) {
        clearInterval(resendTimerIntervalRef.current);
      }
    };
  }, []);

  const finalizeRegistration = async (formData) => {
    const { username, email, password, phoneNumber, referral, country } = formData;

    // Verify referral code if provided
    if (referral && referral.trim()) {
      const verified = await verifyReferralCode(referral);
      if (!verified) {
        setIsLoading(false);
        showError('Please enter valid referral code');
        return false;
      }
    }

    try {
      const registrationData = {
        username: username || '',
        password: password,
        email: email,
        phone: phoneNumber || '',
        method: true,
        refree: referral || '',
        country: country || '',
        aff: storage.getKey('aff') ? storage.getKey('aff') : null
      };

      console.log('Sending registration data to server:', {
        ...registrationData,
        password: '[HIDDEN]'
      });

      const currentSocket = getSocket();
      if (!currentSocket) {
        setIsLoading(false);
        showError('Connection error. Please refresh the page.');
        return false;
      }

      currentSocket.emit(C.REGISTER_USER, encode(registrationData));
      console.log('Registration data sent to server');
      return true;
    } catch (error) {
      console.error('Registration error:', error);
      setIsLoading(false);
      showError('An error occurred during registration. Please try again.');
      return false;
    }
  };

  const handleRegisterSubmit = async (formData) => {
    const { username, email, password, ruleChecked } = formData;

    // Required fields validation - only email, password, and terms
    if (!email || !password || !ruleChecked) {
      showError('Email, password, and Terms of Service agreement are required');
      return false;
    }

    // Email validation
    if (!isEmail(email)) {
      showError('Please enter valid email address');
      return false;
    }

    // Password validation
    if (password.length < 8) {
      showError('Password must be at least 8 characters long');
      return false;
    }

    const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!strongPasswordRegex.test(password)) {
      showError('Password must contain uppercase, lowercase, number, and special character');
      return false;
    }

    // Username validation (optional, but if provided, validate it)
    if (username && username.trim()) {
      if (username.length < 5) {
        showError('Username must be at least 5 characters');
        return false;
      }

      if (username.length > 16) {
        showError('Username must be less than or equal to 16 characters');
        return false;
      }

      // Check if username matches email local part
      const emailLocalPart = email.split('@')[0];
      if (emailLocalPart.toLowerCase() === username.toLowerCase()) {
        showError('Username and email name cannot be the same');
        return false;
      }
    }

    setIsLoading(true);

    // Skip OTP verification and directly proceed with registration
    return await finalizeRegistration(formData);
  };

  return {
    isLoading,
    setIsLoading,
    showOtpSection,
    setShowOtpSection,
    otp,
    setOtp,
    otpSent,
    otpVerified,
    resendTimer,
    sendOtp,
    verifyOtp,
    resendOtp,
    handleRegisterSubmit,
    showError,
    showInfo,
  };
};

export default useRegister;
